import { useState } from 'react'
import { MapPin, Star, Building2 } from 'lucide-react'
import { Body, Caption } from '@meiuca/ui-components'
import { Arena, formatDistance } from '../types/arena'
import { getGameModalityLabel } from '../types/game'

interface ArenaCardProps {
  arena: Arena
  onClick?: () => void
}

export function ArenaCard({ arena, onClick }: ArenaCardProps) {
  const [coverFailed, setCoverFailed] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const showCover = !!arena.coverImage && !coverFailed

  return (
    <div
      onClick={onClick}
      className="flex-shrink-0 w-[280px] rounded-lg overflow-hidden bg-background-white border border-border-light hover:shadow-lg transition-shadow cursor-pointer"
    >
      {/* Cover Image - 2:1 ratio (placeholder mirrors mobile: brand gradient + building icon) */}
      <div className="relative w-full overflow-hidden" style={{ paddingTop: '50%', height: 0 }}>
        {showCover ? (
          <img
            src={arena.coverImage}
            alt={arena.name}
            onError={() => setCoverFailed(true)}
            className="absolute top-0 left-0 w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            style={{ maxHeight: 'none' }}
          />
        ) : (
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-brand-primary/30 to-brand-primary/10 flex items-center justify-center">
            <Building2 className="w-10 h-10 text-brand-primary/40" />
          </div>
        )}
        {/* Logo overlay */}
        {arena.logo && !logoFailed && (
          <div className="absolute bottom-2 left-2 w-12 h-12 rounded-lg overflow-hidden bg-white border-2 border-white shadow-md">
            <img
              src={arena.logo}
              alt={`${arena.name} logo`}
              onError={() => setLogoFailed(true)}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        {arena.rating && (
          <div className="absolute top-2 right-2 bg-background-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <Caption className="font-semibold text-text-primary">
              {arena.rating.toFixed(1)}
            </Caption>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-2">
        {/* Nome */}
        <Body className="font-semibold text-text-primary line-clamp-1">
          {arena.name}
        </Body>

        {/* Modalidades */}
        <div className="flex flex-wrap gap-1">
          {arena.modalities.slice(0, 3).map((modality, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-primary/10 text-brand-primary"
            >
              {getGameModalityLabel(modality)}
            </span>
          ))}
          {arena.modalities.length > 3 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-text-secondary">
              +{arena.modalities.length - 3}
            </span>
          )}
        </div>

        {/* Endereço */}
        {arena.address && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-text-tertiary flex-shrink-0 mt-0.5" />
            <Caption className="text-text-secondary line-clamp-2">
              {arena.address}
            </Caption>
          </div>
        )}

        {/* Localização (fallback se não tiver endereço) */}
        {!arena.address && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-text-tertiary flex-shrink-0 mt-0.5" />
            <Caption className="text-text-secondary line-clamp-1">
              {arena.location}
            </Caption>
          </div>
        )}

        {/* Distância e Link Mapa */}
        <div className="flex items-center justify-between pt-2 border-t border-border-light">
          {arena.distance > 0 ? (
            <>
              <Caption className="text-text-tertiary">Distância</Caption>
              <Caption className="font-semibold text-brand-primary">
                {formatDistance(arena.distance)}
              </Caption>
            </>
          ) : (
            <Caption className="text-text-tertiary">Localização</Caption>
          )}

          {arena.coordinate && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                const url = `https://www.google.com/maps/search/?api=1&query=${arena.coordinate!.latitude},${arena.coordinate!.longitude}`
                window.open(url, '_blank')
              }}
              className="text-brand-primary hover:text-brand-primary/80 transition-colors text-xs font-medium"
            >
              Ver no mapa →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
